import React, { useEffect, useState } from "react";
import { getBracket, listTeams } from "../lib/api";
import { Loader2, Swords, Trophy } from "lucide-react";

const ROUND_LABELS = ["", "Round of 16", "Quarterfinals", "Semifinals", "Final"];

const TeamSlot = ({ team, isWinner, score }) => (
  <div
    className={`flex items-center justify-between gap-2 px-3 py-2 transition-all ${
      isWinner
        ? "bg-[#FF5E00]/15 border-l-2 border-[#FF5E00] text-white"
        : team
        ? "text-white/85"
        : "text-white/30"
    }`}
  >
    <div className="flex items-center gap-2 min-w-0">
      {team?.logo && (
        <img src={team.logo} alt="" className="h-5 w-5 rounded bg-[#1E1233]" />
      )}
      <span className="font-display uppercase text-xs tracking-wider truncate">
        {team?.name || "N/A"}
      </span>
    </div>
    <span className={`font-mono text-xs ${isWinner ? "text-[#FF5E00]" : "text-white/40"}`}>
      {score ?? "—"}
    </span>
  </div>
);

const MatchCard = ({ match, teamMap }) => {
  const t1 = teamMap[match.team1_id];
  const t2 = teamMap[match.team2_id];
  const w = match.winner_id;
  return (
    <div
      className="glass-card rounded-md overflow-hidden w-56 shadow-[0_4px_20px_rgba(0,0,0,0.4)] hover:border-[#FF5E00] transition-all"
      data-testid={`match-card-${match.round}-${match.position}`}
    >
      <div className="bg-[#1E1233]/80 px-3 py-1.5 flex items-center justify-between border-b border-[#9D4CDD]/20">
        <span className="font-display uppercase text-[10px] tracking-[0.2em] text-[#9D4CDD]">
          M{match.round}.{match.position + 1}
        </span>
        {w && <Trophy className="w-3 h-3 text-[#FF5E00]" />}
      </div>
      <div className="divide-y divide-[#9D4CDD]/15">
        <TeamSlot team={t1} isWinner={w === match.team1_id} score={w ? match.score1 : null} />
        <TeamSlot team={t2} isWinner={w === match.team2_id} score={w ? match.score2 : null} />
      </div>
    </div>
  );
};

const RoundColumn = ({ round, matches, teamMap }) => (
  <div className="flex flex-col justify-around min-h-full" data-testid={`round-col-${round}`}>
    <div className="text-center mb-4">
      <div className="font-display uppercase text-xs tracking-[0.3em] text-[#FF5E00]">
        {ROUND_LABELS[round]}
      </div>
      <div className="text-white/40 text-[10px] font-mono">{matches.length} {matches.length === 1 ? "match" : "matches"}</div>
    </div>
    <div className="flex flex-col gap-6 flex-1 justify-around">
      {matches.map((m) => (
        <MatchCard key={m.id} match={m} teamMap={teamMap} />
      ))}
    </div>
  </div>
);

export default function Bracket() {
  const [bracket, setBracket] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getBracket(), listTeams()])
      .then(([b, t]) => {
        setBracket(b);
        setTeams(t);
      })
      .finally(() => setLoading(false));
  }, []);

  const teamMap = {};
  teams.forEach((t) => (teamMap[t.id] = t));

  const byRound = [1, 2, 3, 4].map((r) =>
    bracket.filter((m) => m.round === r).sort((a, b) => a.position - b.position)
  );

  const champion = bracket.find((m) => m.round === 4)?.winner_id
    ? teamMap[bracket.find((m) => m.round === 4).winner_id]
    : null;

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="bracket-page">
      <div className="mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF5E00]/40 bg-[#FF5E00]/5 text-[#FF5E00] text-xs uppercase tracking-[0.25em] font-display mb-4">
          <Swords className="w-3 h-3" /> Championship Bracket
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">
          Top 16 <span className="text-gradient-orange">Bracket</span>
        </h1>
        <p className="mt-3 text-white/65 max-w-2xl">
          Single-elimination — Round of 16 to the Final. Winners advance live.
        </p>
      </div>

      {loading ? (
        <div className="py-24 flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#FF5E00]" />
        </div>
      ) : bracket.length === 0 ? (
        <div className="glass-card rounded-xl p-12 text-center">
          <Swords className="w-10 h-10 mx-auto text-[#9D4CDD] mb-4" />
          <h3 className="font-display uppercase text-2xl font-bold mb-2">
            Bracket Not Started
          </h3>
          <p className="text-white/60 max-w-md mx-auto">
            The championship bracket has not been generated yet. An admin needs to
            seed the top 16 from Standings.
          </p>
        </div>
      ) : (
        <>
          {champion && (
            <div className="glass-card rounded-xl p-6 mb-10 flex items-center gap-5 border-[#FF5E00]/60">
              <div className="h-16 w-16 rounded-lg bg-[#FF5E00]/20 flex items-center justify-center">
                <Trophy className="w-8 h-8 text-[#FF5E00]" />
              </div>
              <div>
                <div className="font-display uppercase text-xs tracking-[0.3em] text-[#FF5E00]">
                  Champion
                </div>
                <div className="font-display uppercase text-3xl font-black mt-1">
                  {champion.name}
                </div>
              </div>
            </div>
          )}
          <div className="overflow-x-auto pb-6">
            <div className="flex gap-10 min-w-fit items-stretch">
              {byRound.map((matches, idx) => (
                <RoundColumn
                  key={idx + 1}
                  round={idx + 1}
                  matches={matches}
                  teamMap={teamMap}
                />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
